import { useEffect, useState } from 'react';
import type { Me, OrganizationRole, Role } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Founder view-as toggle
//
// Client-side role impersonation for platform founders ONLY. Reads/writes
// a sessionStorage key. The UI uses the effective role (and, for surfaces
// that gate on more than the role scalar, the effective `me` — see
// useEffectiveMe below) to filter nav, labels, and role-gated affordances.
//
// SCOPE (corrected 2026-08-02 — the old comment here said "UI-only, no
// backend enforcement", which is no longer true): this module is the CLIENT
// half of view-as. Server-side enforcement now exists as its own piece — the
// request carries the simulation, the API refuses writes above the simulated
// persona, and the attempt is audit-logged. See docs/product/VIEW-AS-TOGGLE.md,
// whose "Server" section is now built rather than target-state.
//
// What this module is still responsible for, and what it is NOT: it decides
// what the founder's own client RENDERS. It is not the authorization
// boundary and never was — the server is. Treat a leak here as a
// confidentiality bug (the founder sees data the persona could not), and a
// leak there as an authorization bug. The two halves must agree, and the
// tests in viewAs.test.ts pin this half against the same role spine
// (src/shared/roleSpine.ts) the server resolves against.
//
// Rule: never expose this hook to any code path reachable by non-founder
// users. The only gate is `me.role === 'founder'` in Sidebar.tsx (guards
// the ViewAsSwitcher mount) — non-founders never write a view-as session,
// so every hook here is a pure identity no-op for them.
// ─────────────────────────────────────────────────────────────────────────────

const KEY = 'ggm_view_as';
const CHANGE_EVENT = 'ggm_view_as_change';

/**
 * The discordId every AUTHENTICATED persona carries. Deliberately NOT a
 * numeric string: a real Discord id is a numeric snowflake, so this can never
 * collide with a real user's row — the persona reads as "logged in, but
 * nobody in particular", which is exactly what a simulated stranger is.
 *
 * Why the founder's real id can't stay: it IS a grant, and the most powerful
 * one on the platform. Every "is this row mine?" check — PublicMatchCard's
 * organiser/side-agreement logic, MatchCardLive's slot ownership, PlayerCard —
 * compares against me.discordId. Leaving the real id in place meant a preview
 * that had carefully stripped every membership still matched the founder's own
 * matches, seats and cards.
 *
 * KNOWN CONSEQUENCE, accepted deliberately: pages that fetch by the caller's
 * own id (their player card, their seat) show empty/not-found under a preview.
 * That is the honest answer for a persona who is not a real user — far better
 * than a preview quietly confirming the founder's own access.
 */
export const VIEW_AS_SIM_DISCORD_ID = 'view-as-simulated-user';

/** Org-scoped simulation target — a real scoped org manager's shape (e.g.
 *  Queeni: platform role 'member' + ONE org membership), as opposed to the
 *  platform-role sim below (a cross-org platform tier with no org
 *  memberships at all). */
export type ViewAsOrgAdmin = {
  orgId: string;
  orgName: string;
};

/** Clan-scoped simulation target. `teamNo` is meaningful for CLAN_CAPTAIN
 *  only — roleSpine.ts pins a captain to ONE team (a captain of team 3 has
 *  zero standing over team 4), while a leader runs every team in the clan. */
export type ViewAsClan = {
  clanId: string;
  clanName: string;
  teamNo?: number | null;
};

/** The org rank a scoped org preview simulates. ORG_OWNER is NOT cosmetic:
 *  it exclusively holds org.view_billing / org.manage_billing /
 *  org.manage_roles (src/shared/roleSpine.ts's CAPABILITY_MIN_RANK), so it
 *  is the only persona that can preview those screens at all. */
export type ViewAsOrgRank = 'ORG_ADMIN' | 'ORG_OWNER';

/** The clan rank a scoped clan preview simulates (roleSpine RoleRank
 *  CLAN_LEADER=3 / CLAN_CAPTAIN=2). */
export type ViewAsClanRank = 'CLAN_LEADER' | 'CLAN_CAPTAIN';

export type ViewAsState = {
  /** The platform role being rendered. When `org` or `clan` is set this is
   *  written by the scoped enter* setters below, never chosen directly from
   *  the bare role list. */
  role: Role;
  startedAt: number;
  /** Present only for the org-scoped modes. */
  org?: ViewAsOrgAdmin;
  /** Which org rank `org` simulates. ABSENT means ORG_ADMIN — sessions
   *  written before ORG_OWNER previewing existed carry `org` with no
   *  `orgRole`, and a stored blob is never allowed to fail OPEN into the
   *  more powerful tier. */
  orgRole?: ViewAsOrgRank;
  /** Present only for the clan-scoped modes. */
  clan?: ViewAsClan;
  /** Which clan rank `clan` simulates. Absent means CLAN_LEADER. */
  clanRole?: ViewAsClanRank;
};

export function getViewAs(): ViewAsState | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ViewAsState;
    if (!parsed.role) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setViewAs(state: ViewAsState) {
  sessionStorage.setItem(KEY, JSON.stringify(state));
  // Tell hooks in the current tab to re-read (storage event only fires
  // cross-tab, not same-tab)
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function clearViewAs() {
  sessionStorage.removeItem(KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * React hook — returns current view-as state and setters. Syncs across
 * components via a window event.
 */
export function useViewAs() {
  const [state, setState] = useState<ViewAsState | null>(() => getViewAs());

  useEffect(() => {
    const sync = () => setState(getViewAs());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return {
    viewAs: state,
    enter: (role: Role) => setViewAs({ role, startedAt: Date.now() }),
    /** "Org admin (scoped)" mode — simulates ONE real org's ORG_ADMIN
     *  membership instead of a platform-wide role. See useEffectiveMe. */
    enterOrgAdmin: (orgId: string, orgName: string) =>
      setViewAs({ role: 'member', org: { orgId, orgName }, orgRole: 'ORG_ADMIN', startedAt: Date.now() }),
    /** "Org owner (scoped)" mode — one rung above the admin sim, and the
     *  ONLY way to preview billing + role-management screens. */
    enterOrgOwner: (orgId: string, orgName: string) =>
      setViewAs({ role: 'member', org: { orgId, orgName }, orgRole: 'ORG_OWNER', startedAt: Date.now() }),
    /** "Clan leader (scoped)" mode — platform tier 'leader' PLUS a real clan
     *  placement, so clan-scoped surfaces resolve against a clan instead of
     *  a bare label. */
    enterClanLeader: (clanId: string, clanName: string) =>
      setViewAs({ role: 'leader', clan: { clanId, clanName }, clanRole: 'CLAN_LEADER', startedAt: Date.now() }),
    /** "Clan captain (scoped)" mode — a captaincy is an OVERLAY on a plain
     *  member (see Me.captaincies' own doc comment), pinned to ONE team. */
    enterClanCaptain: (clanId: string, clanName: string, teamNo: number) =>
      setViewAs({
        role: 'member',
        clan: { clanId, clanName, teamNo },
        clanRole: 'CLAN_CAPTAIN',
        startedAt: Date.now(),
      }),
    exit: () => clearViewAs(),
  };
}

/**
 * Returns the role the UI should render as — view-as role if active,
 * otherwise the real role from the /me response.
 */
export function useEffectiveRole(realRole: Role | undefined): Role | undefined {
  const { viewAs } = useViewAs();
  return viewAs?.role ?? realRole;
}

/**
 * Returns a view-as-adjusted copy of `me` — for surfaces that gate on more
 * than the role scalar (org memberships): Sidebar's Run It org-manager
 * bypass, TournamentSetup/ClanManagement's org-admin de-gate + org/comp
 * selector scoping, MyCompDetail's canManageClans, MatchCardLive's
 * canManageMatchOrg.
 *
 * IDENTITY GUARANTEE (load-bearing, this is the whole safety story): when
 * no view-as is active, this returns `me` completely UNCHANGED. Non-
 * founders never have a view-as selection (the switcher only ever mounts
 * behind `me.role === 'founder'` in Sidebar.tsx), and a founder who isn't
 * currently previewing gets the same identity pass-through — so this hook
 * is a true no-op for every real user and every non-previewing founder.
 * Real access control is NEVER touched by it: the backend enforces against
 * the REAL token regardless of what this returns. This only changes what
 * the founder's OWN client renders while previewing.
 *
 *   • view-as a platform role R -> role R with NO scoped grants at all.
 *   • view-as org admin/owner of org X -> platform tier 'member' plus
 *     EXACTLY one org membership at X — a real scoped org manager's shape.
 *   • view-as clan leader/captain of clan C -> a clan placement (and, for a
 *     captain, the team-pinned captaincy overlay a real captain carries).
 */
export function useEffectiveMe(me: Me | null | undefined): Me | null | undefined {
  const { viewAs } = useViewAs();
  return applyViewAs(me, viewAs);
}

/**
 * The simulation engine — pure, so the personas can be proven without React
 * (see viewAs.test.ts). useEffectiveMe is this function plus the hook that
 * reads the current session.
 *
 * EVERY simulation starts from a persona with NO scoped grants and rebuilds
 * only what the chosen persona genuinely holds. That is the whole correction
 * of 2026-08-02: the previous version zeroed `orgMemberships` but carried the
 * founder's REAL `competitionMemberships` and `captaincies` straight into the
 * persona, so a "member" preview was the founder wearing a different label —
 * MyComp listed their real comps and MyCompDetail opened manage-clans off a
 * real COMP_OWNER row. A preview that shows MORE than the persona can see
 * doesn't just fail to catch leaks, it launders them.
 *
 * `discordId` is simulated away too — it is a grant, not decoration; see
 * VIEW_AS_SIM_DISCORD_ID. Deliberately NOT simulated away: the DISPLAY
 * identity (displayName, discordProfile, credit balances). Those are cosmetic
 * rather than gate-reading, and masking them in the sidebar is the
 * ViewAsBanner/Sidebar surface's own job, not this engine's.
 */
export function applyViewAs(me: Me | null | undefined, viewAs: ViewAsState | null): Me | null | undefined {
  // IDENTITY GUARANTEE — the same object back, untouched. Load-bearing.
  if (!viewAs || !me) return me;

  const sim: Me = {
    ...me,
    role: viewAs.role,
    // 'public' means UNAUTHENTICATED, so the persona carries no id at all —
    // falsy, because that is what every `if (me.discordId)` check on the
    // platform reads as "not logged in". Authenticated personas get the
    // synthetic id above. See VIEW_AS_SIM_DISCORD_ID for the full reasoning.
    discordId: viewAs.role === 'public' ? '' : VIEW_AS_SIM_DISCORD_ID,
    orgMemberships: [],
    competitionMemberships: [],
    captaincies: [],
    simulatedClanScope: null,
  };

  if (viewAs.org) {
    // Absent orgRole means a session written before ORG_OWNER previewing
    // existed — read it as the LOWER tier (fail-closed on stored state).
    const role: OrganizationRole = viewAs.orgRole === 'ORG_OWNER' ? 'ORG_OWNER' : 'ORG_ADMIN';
    sim.role = 'member';
    sim.orgMemberships = [{ orgId: viewAs.org.orgId, role }];
  }

  if (viewAs.clan) {
    const scopedRole: ViewAsClanRank = viewAs.clanRole === 'CLAN_CAPTAIN' ? 'CLAN_CAPTAIN' : 'CLAN_LEADER';
    // A leader runs every team, so it carries no team pin; a captain without
    // one is not a captain at all, so fall back to team 1 rather than invent
    // an unpinned captain the backend could never produce.
    const teamNo = scopedRole === 'CLAN_CAPTAIN' ? (viewAs.clan.teamNo ?? 1) : null;
    // A captaincy is an overlay on a plain member (Me.captaincies' doc
    // comment); a clan LEADER is its own platform tier.
    sim.role = scopedRole === 'CLAN_LEADER' ? 'leader' : 'member';
    sim.clanName = viewAs.clan.clanName;
    sim.simulatedClanScope = {
      clanId: viewAs.clan.clanId,
      clanName: viewAs.clan.clanName,
      scopedRole,
      teamNo,
    };
    if (scopedRole === 'CLAN_CAPTAIN' && teamNo !== null) {
      sim.captaincies = [{ clanName: viewAs.clan.clanName, teamNo, kind: 'PERMANENT' }];
    }
  }

  return sim;
}
